import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useTable, useResizeColumns } from "react-table";
import Popover from "react-bootstrap/Popover";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";

import "./advancedTable.css";
import { DATA_NOT_FOUND } from '../../../constant';
import SkeletonLoading from '../skeletonLoading/skeletonLoading';
import PrimaryButton from '../primaryButton/primaryButton';

const EMPTY_ARRAY = [];
const NO_OP = () => {};

const AdvancedTable = ({
  columns,
  data,
  isLoading,
  handleLoadMore,
  onClickDetails,
  rowCount,
  pagination = true,
  highlightRowIds = EMPTY_ARRAY,
  onDetailsFullData = false,
  // Row selection props
  enableRowSelection = false,
  selectedRows = EMPTY_ARRAY,
  onRowSelectionChange = NO_OP,
  rowIdKey = "id",
  enableBulkActions = false,
}) => {
  const [internalSelectedRows, setInternalSelectedRows] = useState(EMPTY_ARRAY);

  useEffect(() => {
    if (selectedRows !== internalSelectedRows) {
      setInternalSelectedRows(selectedRows);
    }
  }, [selectedRows, internalSelectedRows]);

  const currentSelectedRows = useMemo(() => {
    return selectedRows.length > 0 ? selectedRows : internalSelectedRows;
  }, [selectedRows, internalSelectedRows]);

  const isAllSelected = useMemo(() => {
    if (data.length === 0) return false;
    return data.every(row => currentSelectedRows.includes(row[rowIdKey]));
  }, [data, currentSelectedRows, rowIdKey]);

  const isSomeSelected = useMemo(() => {
    return currentSelectedRows.length > 0 && !isAllSelected;
  }, [currentSelectedRows, isAllSelected]);

  const handleSelectAll = useCallback(() => {
    let newSelection;
    if (isAllSelected) {
      const currentPageIds = data.map(row => row[rowIdKey]);
      newSelection = currentSelectedRows.filter(id => !currentPageIds.includes(id));
    } else {
      const currentPageIds = data.map(row => row[rowIdKey]);
      const uniqueIds = new Set([...currentSelectedRows, ...currentPageIds]);
      newSelection = Array.from(uniqueIds);
    }
    setInternalSelectedRows(newSelection);
    onRowSelectionChange(newSelection);
  }, [isAllSelected, data, rowIdKey, currentSelectedRows, onRowSelectionChange]);

  const handleRowToggle = useCallback((rowId) => {
    let newSelection;
    if (currentSelectedRows.includes(rowId)) {
      newSelection = currentSelectedRows.filter(id => id !== rowId);
    } else {
      newSelection = [...currentSelectedRows, rowId];
    }
    setInternalSelectedRows(newSelection);
    onRowSelectionChange(newSelection);
  }, [currentSelectedRows, onRowSelectionChange]);

  const checkboxColumn = useMemo(
    () => ({
      id: "selection",
      Header: () => (
        <div className="advanced-table-checkbox-header-top">
          <input
            type="checkbox"
            checked={isAllSelected}
            ref={(input) => {
              if (input) input.indeterminate = isSomeSelected;
            }}
            onChange={handleSelectAll}
            className="advanced-table-checkbox"
          />
          <span className="advanced-table-checkbox-label">
            {currentSelectedRows.length > 0
              ? `Selected (${currentSelectedRows.length})`
              : "Select"}
          </span>
        </div>
      ),
      Cell: ({ row }) => (
        <div className="advanced-table-checkbox-cell">
          <input
            type="checkbox"
            checked={currentSelectedRows.includes(row.original[rowIdKey])}
            onChange={() => handleRowToggle(row.original[rowIdKey])}
            className="advanced-table-checkbox"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ),
      minWidth: 110,
      maxWidth: 110,
      disableResizing: true,
    }),
    [isAllSelected, isSomeSelected, currentSelectedRows, handleSelectAll, handleRowToggle, rowIdKey]
  );

  const tableColumns = useMemo(() => {
    return enableRowSelection ? [checkboxColumn, ...columns] : columns;
  }, [enableRowSelection, checkboxColumn, columns]);

  const table = useTable(
    { columns: tableColumns, data: data },
    useResizeColumns
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = table;

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    const threshold = 150;
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + threshold;
    if (pagination && isNearBottom && !isLoading && data.length < rowCount) {
      handleLoadMore();
    }
  };

  const TruncatedCellWithTooltip = useCallback(({ value }) => {
    const displayValue = value ?? "—";
    const fullText = String(displayValue);
    const shouldTruncate = fullText.length > 35;
    const truncated = shouldTruncate ? fullText.slice(0, 30) + "…" : fullText;

    if (!shouldTruncate) {
      return <div className="truncated-cell">{truncated}</div>;
    }

    const popover = (
      <Popover id="popover-long-text" className="long-text-popover" style={{ maxWidth: "560px" }}>
        <Popover.Body className="p-3">
          <div style={{ maxHeight: "380px", overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "0.93rem", lineHeight: "1.45" }}>
            {fullText}
          </div>
        </Popover.Body>
      </Popover>
    );

    return (
      <OverlayTrigger
        trigger="click"
        placement="auto"
        rootClose
        overlay={popover}
        popperConfig={{
          modifiers: [
            { name: "preventOverflow", options: { boundary: "viewport", altBoundary: true, tether: true, tetherOffset: 10 } },
            { name: "flip", options: { fallbackPlacements: ["top", "bottom", "right", "left"] } },
            { name: "offset", options: { offset: [0, 8] } },
          ],
        }}
      >
        <div className="truncated-cell">{truncated}</div>
      </OverlayTrigger>
    );
  }, []);

  return (
    <div className="advanced-table-main-div" onScroll={handleScroll}>
      <table {...getTableProps()} className="advanced-table-main">
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id} className="advanced-table-head-row">
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps()}
                  key={column.id}
                  className={column.hideOnSmallScreen
                    ? "advanced-table-head-th advanced-table-head-th-hide-small-screen"
                    : "advanced-table-head-th"}
                  style={{ minWidth: column.minWidth, maxWidth: column.maxWidth }}
                >
                  <div className="d-flex" style={{ justifyContent: "space-between", alignItems: "center" }}>
                    <div>{column.render("Header")}</div>
                    {column.isFilter && (
                      <div>
                        {column.render("popoverFilter", {
                          title: "Filter " + (typeof column.Header === 'string' ? column.Header : ""),
                          filterList: column.filterDataList,
                          formControl: column.control,
                          inputKey: column.inputKey,
                          placeholder: column.placeholder,
                          onChange: column.onChange,
                          filterType: column.filterType,
                          defaultValue: column.defaultValue,
                          currentValue: column.currentValue,
                        })}
                      </div>
                    )}
                  </div>
                  {column.currentValue && (
                    <div className="filter-active-label">{column.currentValue}</div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            const isHighlightRow = highlightRowIds.indexOf(row.original.id) > -1;
            return (
              <tr
                {...row.getRowProps()}
                key={row.id}
                className={isHighlightRow
                  ? "advanced-table-body-row advanced-table-body-row-hightlight"
                  : "advanced-table-body-row"}
              >
                {row.cells.map((cell) => {
                  const { onClick, hideOnSmallScreen, isToolTipCell } = cell.column;
                  let cellContent = cell.render("Cell");
                  if (isToolTipCell) {
                    cellContent = <TruncatedCellWithTooltip value={cell.value} />;
                  }
                  return (
                    <td
                      {...cell.getCellProps()}
                      key={cell.id}
                      onClick={onClick ? () => onClickDetails(onDetailsFullData ? row.original : row.original.id) : () => {}}
                      className={
                        (onClick ? "advanced-table-onclick-td" : "advanced-table-td") +
                        (hideOnSmallScreen ? " advanced-table-head-th-hide-small-screen" : "")
                      }
                    >
                      {cellContent}
                    </td>
                  );
                })}
              </tr>
            );
          })}

          {isLoading && [...Array(6)].map((_, i) => (
            <tr className="advanced-table-body-row" key={`skel-${i}`}>
              {[...Array(columns.length)].map((_, j) => (
                <td key={j} className="advanced-table-td">
                  <SkeletonLoading customStyle={{ width: j === 0 ? "55%" : "75%", height: "14px", borderRadius: "6px" }} />
                </td>
              ))}
            </tr>
          ))}

          {pagination && !isLoading && data.length < rowCount && (
            <tr key="load_more_button" className="advanced-table-load-more-row">
              <td colSpan={tableColumns.length}>
                <PrimaryButton text="Load More" onClick={handleLoadMore} isLoading={isLoading} />
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {!isLoading && rowCount === 0 && (
        <div className="advanced-table-empty-state">
          <img src={DATA_NOT_FOUND} alt="no record found" />
          <p className="advanced-table-empty-title">No Data Found</p>
          <p className="advanced-table-empty-subtitle">Try adjusting your filters or search criteria.</p>
        </div>
      )}
    </div>
  );
};

export default AdvancedTable;

